"use client";

import { ArrowRightAlt } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import useEmblaCarousel from "embla-carousel-react";
import { type ComponentProps, useCallback, useEffect, useState } from "react";
import {
	ArticleCard,
	type ArticleCardSize,
	type ArticleCardVariant,
} from "@/components/ui/ArticleCard";
import { Button } from "@/components/ui/Button";
import { Container } from "@/components/ui/Container";
import { Link } from "@/components/ui/Link";
import { Scrollbar } from "@/components/ui/Scrollbar";
import { Typography } from "@/components/ui/Typography";
import type { StoriesRoute } from "@/data/routes/stories.route";
import { Link as NextLink } from "@/i18n/routing";
import { isDarkMode } from "@/lib/utils/isDarkMode";

export type ArticleSectionVariant =
	| "slider"
	| "grid"
	| "gridOneRow"
	| "twoCards"
	| "horizontalHighlight";

type ArticleItem = NonNullable<StoriesRoute["articles"]>[number];

export interface ArticleSectionProps extends ComponentProps<"section"> {
	variant?: ArticleSectionVariant;
	caption?: string;
	heading?: string;
	description?: string;
	linkLabel?: string;
	linkHref?: string;
	items: StoriesRoute["articles"];
	loadMoreLabel?: string;
	bg?: string;
}

const DEFAULT_INITIAL_COUNT = 6;

const Header = ({
	caption,
	heading,
	description,
	linkLabel,
	linkHref,
	darkMode,
}: {
	caption?: string;
	heading?: string;
	description?: string;
	linkLabel?: string;
	linkHref?: string;
	darkMode?: boolean;
}) => {
	if (!caption && !heading && !description && !(linkLabel && linkHref)) return null;
	return (
		<div className="flex w-full flex-col gap-grid-component-spacing-md text-text-dark-text-1">
			<div className="flex w-full items-end gap-grid-component-spacing-2xs">
				<div className="flex min-w-px flex-1 flex-col gap-grid-component-spacing-md">
					{caption && (
						<Typography
							variant="label-sm-reg"
							as="p"
							className={clsx("max-w-[32rem] leading-[1.15]", darkMode && "text-text-light-text-1")}
						>
							{caption}
						</Typography>
					)}
					{heading && (
						<Typography
							variant="heading-display-md"
							as="h2"
							className={clsx("max-w-[32rem]", darkMode && "text-text-light-text-1")}
						>
							{heading}
						</Typography>
					)}
				</div>
				{linkLabel && linkHref && (
					<Link
						as={NextLink}
						href={linkHref}
						variant="primary"
						size="medium"
						className={clsx(darkMode && "text-text-light-text-1")}
					>
						{linkLabel}
					</Link>
				)}
			</div>
			{description && (
				<Typography
					variant="body-sm-reg"
					as="p"
					className={clsx("max-w-[32rem]", darkMode && "text-text-light-text-1")}
				>
					{description}
				</Typography>
			)}
		</div>
	);
};

type CardProps = {
	item: ArticleItem;
	variant?: ArticleCardVariant;
	size?: ArticleCardSize;
	sizes?: string;
};

const Card = ({ item, variant = "portrait", size = "medium", sizes }: CardProps) => (
	<ArticleCard
		variant={variant}
		size={size}
		image={item.image}
		tags={item.tags}
		title={item.title}
		description={item.description}
		slug={item.slug}
		label={item.label}
		sizes={sizes}
	/>
);

const SliderLayout = ({ items }: { items: ArticleItem[] }) => {
	const needsCarousel = items.length > 1;
	const [emblaRef, emblaApi] = useEmblaCarousel({
		active: needsCarousel,
		align: "start",
		containScroll: "trimSnaps",
		breakpoints: needsCarousel ? { "(max-width: 639px)": { align: "center" as const } } : undefined,
	});
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [snapCount, setSnapCount] = useState(0);

	useEffect(() => {
		if (!emblaApi) return;
		const sync = () => {
			setSelectedIndex(emblaApi.selectedScrollSnap());
			setSnapCount(emblaApi.scrollSnapList().length);
		};
		emblaApi.on("select", sync);
		emblaApi.on("reInit", sync);
		sync();
		return () => {
			emblaApi.off("select", sync);
			emblaApi.off("reInit", sync);
		};
	}, [emblaApi]);

	const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
	const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

	return (
		<div className="flex flex-col gap-grid-component-spacing-md">
			<div ref={emblaRef} className="w-full overflow-hidden">
				<div className="flex items-start gap-grid-grid-gutter-md [&>*:last-child]:mr-4 sm:[&>*:last-child]:mr-0">
					{items.map((item, i) => (
						<div key={i} className="shrink-0 grow-0 basis-[75%] sm:basis-[45%] lg:basis-1/4">
							<Card item={item} sizes="(max-width: 640px) 75vw, (max-width: 1024px) 45vw, 25vw" />
						</div>
					))}
				</div>
			</div>
			{snapCount > 1 && (
				<Scrollbar
					current={selectedIndex + 1}
					total={snapCount}
					onPrev={scrollPrev}
					onNext={scrollNext}
				/>
			)}
		</div>
	);
};

const GridLayout = ({ items, loadMoreLabel }: { items: ArticleItem[]; loadMoreLabel?: string }) => {
	const [visible, setVisible] = useState(DEFAULT_INITIAL_COUNT);
	const shown = items.slice(0, visible);
	const hasMore = items.length > visible;
	return (
		<div className="flex flex-col items-center gap-grid-section-vertical-md">
			<div className="grid w-full grid-cols-2 gap-x-2 gap-y-grid-section-vertical-md lg:grid-cols-3">
				{shown.map((item, i) => (
					<Card key={i} item={item} sizes="(max-width: 640px) 50vw, 33vw" />
				))}
			</div>
			{hasMore && (
				<Button
					variant="outline"
					color="primary"
					size="large"
					rightIcon={ArrowRightAlt}
					onClick={() => setVisible((n) => n + DEFAULT_INITIAL_COUNT)}
				>
					{loadMoreLabel}
				</Button>
			)}
		</div>
	);
};

const GridOneRowLayout = ({ items }: { items: ArticleItem[] }) => (
	<div className="grid w-full grid-cols-1 gap-x-2 gap-y-grid-section-vertical-md sm:grid-cols-2 lg:grid-cols-3">
		{items.slice(0, 3).map((item, i) => (
			<Card key={i} item={item} sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" />
		))}
	</div>
);

const TwoCardsLayout = ({ items }: { items: ArticleItem[] }) => (
	<div className="grid w-full grid-cols-1 gap-x-2 gap-y-grid-section-vertical-md sm:grid-cols-2">
		{items.slice(0, 2).map((item, i) => (
			<Card
				key={i}
				item={item}
				variant="landscape"
				size="large"
				sizes="(max-width: 640px) 100vw, 50vw"
			/>
		))}
	</div>
);

const HorizontalHighlightLayout = ({ items }: { items: ArticleItem[] }) => {
	const item = items[0];
	if (!item) return null;
	return <Card item={item} variant="horizontal-highlight" sizes="100vw" />;
};

export const ArticleSection = ({
	variant = "grid",
	caption,
	heading,
	description,
	linkLabel,
	linkHref,
	items,
	loadMoreLabel,
	bg,
	...rest
}: ArticleSectionProps) => {
	let content: React.ReactNode;
	switch (variant) {
		case "slider":
			content = <SliderLayout items={items} />;
			break;
		case "grid":
			content = <GridLayout items={items} loadMoreLabel={loadMoreLabel} />;
			break;
		case "gridOneRow":
			content = <GridOneRowLayout items={items} />;
			break;
		case "twoCards":
			content = <TwoCardsLayout items={items} />;
			break;
		case "horizontalHighlight":
			content = <HorizontalHighlightLayout items={items} />;
			break;
	}

	const darkMode = isDarkMode(bg);

	return (
		<section style={bg ? { backgroundColor: bg } : undefined} {...rest}>
			<Container className="py-grid-section-vertical-lg">
				<div className="flex w-full flex-col gap-grid-section-vertical-lg">
					<Header
						caption={caption}
						heading={heading}
						description={description}
						linkLabel={linkLabel}
						linkHref={linkHref}
						darkMode={darkMode}
					/>
					{items.length > 0 && content}
				</div>
			</Container>
		</section>
	);
};
